// File: src/hooks/useProgressSync.ts
// React hook over the ProgressSync singleton.
//
// Responsibilities (intentionally narrow):
//   • Track pending-queue count, isSyncing, lastSync, isOnline as React state.
//   • Auto-flush the queue on `window.online` and on a 30s interval.
//   • Subscribe to the `progressSynced` and `idb:reset` DOM events emitted
//     by the service / IDB wrapper, and refresh the count when they fire.
//   • Expose imperative `flush()` for callers that want to force a sync now.
//
// Out of scope (Session 3 owns these):
//   • Enabling/disabling real-time listeners on auth state change.
//   • Running migrateOnLogin after sign-in.
//   • Wiring values into useAuthStore.syncStatus (this hook just returns
//     them; the store binding lives next to the auth code).

import { useCallback, useEffect, useRef, useState } from 'react';

import { progressSync, type ProgressSync } from '../services/progressSync';
import type { ProcessResult } from '../lib/sync-queue';

/** How often the hook auto-attempts a queue flush, in ms. */
const FLUSH_INTERVAL_MS = 30_000;

export interface UseProgressSyncReturn {
  /** The shared ProgressSync singleton. Use for imperative calls (loadProgress, saveProgress, etc.). */
  progressSync: ProgressSync;
  /** Items currently in the retry queue (whether due or backed off). */
  pendingCount: number;
  /** True while a flush is in flight. */
  isSyncing: boolean;
  /** ms epoch of the last successful flush attempt, or null. */
  lastSync: number | null;
  /** navigator.onLine, kept reactive via online/offline events. */
  isOnline: boolean;
  /** Manually trigger a queue flush. Safe to call when offline or signed-out (no-op). */
  flush: () => Promise<ProcessResult>;
}

const NOOP_RESULT: ProcessResult = { processed: 0, done: 0, failed: 0, remaining: 0 };

export function useProgressSync(): UseProgressSyncReturn {
  const [pendingCount, setPendingCount] = useState<number>(0);
  const [isSyncing, setIsSyncing] = useState<boolean>(false);
  const [lastSync, setLastSync] = useState<number | null>(null);
  const [isOnline, setIsOnline] = useState<boolean>(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );

  // Re-read the queue length from the service.
  const refreshCount = useCallback(async () => {
    try {
      const n = await progressSync.getPendingCount();
      setPendingCount(n);
    } catch (e) {
      console.warn('[useProgressSync] refreshCount failed:', e);
    }
  }, []);

  // Run a flush. Re-entrant guard via syncingRef so concurrent timer + online
  // events don't double-fire (state-based guard would race because setState
  // is async).
  const syncingRef = useRef<boolean>(false);
  const flush = useCallback(async (): Promise<ProcessResult> => {
    if (syncingRef.current) {
      return { ...NOOP_RESULT, remaining: pendingCount };
    }
    syncingRef.current = true;
    setIsSyncing(true);
    try {
      const result = await progressSync.flushQueue();
      setLastSync(Date.now());
      setPendingCount(result.remaining);
      return result;
    } catch (e) {
      console.warn('[useProgressSync] flush failed:', e);
      // Still refresh the count so UI doesn't get stuck on a stale number.
      await refreshCount();
      return { ...NOOP_RESULT, remaining: pendingCount };
    } finally {
      syncingRef.current = false;
      setIsSyncing(false);
    }
  }, [pendingCount, refreshCount]);

  // Stable ref to the latest flush so the effect below doesn't need to
  // re-attach interval/listeners on every render.
  const flushRef = useRef(flush);
  useEffect(() => {
    flushRef.current = flush;
  }, [flush]);

  useEffect(() => {
    // Initial count.
    void refreshCount();

    // Periodic flush.
    const intervalId = window.setInterval(() => {
      void flushRef.current();
    }, FLUSH_INTERVAL_MS);

    // online / offline.
    const onOnline = (): void => {
      setIsOnline(true);
      void flushRef.current();
    };
    const onOffline = (): void => setIsOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);

    // Service emits this when a remote merge lands. Refresh the count in case
    // a different tab also wrote to the queue — keeps the badge honest.
    const onProgressSynced = (): void => {
      void refreshCount();
    };
    document.addEventListener('progressSynced', onProgressSynced);

    // IDB wiped itself due to corruption. The queue went with it.
    const onIdbReset = (): void => {
      console.warn('[useProgressSync] IDB reset — pending queue cleared');
      setPendingCount(0);
      setLastSync(null);
    };
    document.addEventListener('idb:reset', onIdbReset);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      document.removeEventListener('progressSynced', onProgressSynced);
      document.removeEventListener('idb:reset', onIdbReset);
    };
  }, [refreshCount]);

  return {
    progressSync,
    pendingCount,
    isSyncing,
    lastSync,
    isOnline,
    flush,
  };
}